import { Component } from '@angular/core';
import {
  trigger,
  transition,
  style,
  animate,
  query,
  stagger,
} from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SupplierService } from 'src/app/views/settings/suppliers/supplier.service';
import { PoTokenAuthService } from 'src/app/shared/services/po-token-auth.service';

@Component({
  selector: 'app-token-login',
  templateUrl: './po-token-login.component.html',
  styleUrls: ['./po-token-login.component.css'],
  animations: [
    // Page slides in from bottom of the viewport on load
    trigger('pageFadeUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(100vh)' }),
        animate(
          '1200ms cubic-bezier(.22,1,.36,1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
    ]),

    // Staggered fade-up for child items with class .stagger-item
    trigger('staggerFadeUp', [
      transition(':enter', [
        query(
          '.stagger-item',
          [
            style({ opacity: 0, transform: 'translateY(13px)' }),
            stagger(80, [
              animate(
                '350ms ease-out',
                style({ opacity: 1, transform: 'translateY(0)' })
              ),
            ]),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class TokenLoginComponent {
  securityToken = '';
  rfqId!: string;
  quoteId!: string;
  purchaseOrderId!: string;
  loading: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private supplierService: SupplierService,
    private poTokenAuth: PoTokenAuthService
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      this.rfqId = params['rfqId'];
      this.quoteId = params['quoteId'];
      this.purchaseOrderId = params['purchaseOrderId'];
    });
  }

  async verifyToken() {
    let raw = (this.securityToken || '').trim();
    if (!raw) {
      this.snackBar.open('Please enter a valid Security Token', 'Close', { duration: 3000 });
      return;
    }
    // New format comes as: PO-<actual token>-YYYY
    // Send only <actual token> to the backend
    // 1) Strip leading "PO-" (case-insensitive)
    // 2) Strip trailing "-YYYY" (4-digit year) if present
    raw = raw.replace(/^PO-/i, '').replace(/-\d{4}$/i, '');

    this.loading = true;
    try {
      const data: any = await this.supplierService.verifyPoToken(raw);
      if (data?.valid) {
        // Persist Bearer token + PO ID for follow-up API calls
        if (data.poAccessToken && data.poId) {
          this.poTokenAuth.set(data.poAccessToken, data.poId);
        }
        const qp: any = {
          rfqId: data.rfqId ?? this.rfqId,
          quoteId: data.quoteId ?? this.quoteId,
          purchaseOrderId: data.poId ?? this.purchaseOrderId,
          status: 'Submitted',
          supplierId: data.supplierId,
          buyerId: data.buyerId,
        };
        if (data.poNumber) qp.poNumber = data.poNumber;
        this.router.navigate(['/operations/po-token/po-details'], { queryParams: qp });
      } else {
        this.handleVerifyError(data?.reason);
      }
    } catch (err) {
      this.snackBar.open('Verification failed. Please try again.', 'Close', { duration: 3500 });
    } finally {
      this.loading = false;
    }
  }

  private handleVerifyError(reason?: string) {
    const messages: any = {
      missing_token: 'No token provided.',
      server_config_error: 'Server configuration error. Please contact support.',
      invalid_purpose: 'Invalid token purpose.',
      invalid_payload: 'Invalid token payload.',
      expired: 'Link expired. Request a new one.',
      invalid_token: 'Invalid token. Please check and try again.',
    };
    const msg = messages[reason || ''] || 'Unable to verify token.';
    this.snackBar.open(msg, 'Close', { duration: 4000 });
  }
}
